'use client'

import { useState, useMemo, useRef, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useQuery } from '@tanstack/react-query'
import { plansRepository } from '@/lib/repositories/plans.repository'
import type { Plan } from '@/lib/types/plan'
import { FaqSection } from '@/app/components/FaqSection'
import { trackClarityEvent } from '@/lib/clarity'
import { useContactInfo } from '@/lib/hooks/useContactInfo'

export default function AssinaturaV2Page() {
  const router = useRouter()
  const { contactInfo } = useContactInfo()
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [selectedLives, setSelectedLives] = useState<number>(5) // Quantidade de vidas selecionada

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 10)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
    }
  }

  useEffect(() => {
    handleScroll()
    window.addEventListener('resize', handleScroll)
    return () => window.removeEventListener('resize', handleScroll)
  }, [])

  const scrollSlider = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollAmount = container.clientWidth * 0.8
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  // Buscar planos da API
  const { data: plans, isLoading, error } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansRepository.findAll(),
    retry: 2,
  })

  // Função para obter o preço baseado no número de vidas
  const getPriceForLives = (plan: Plan, lives: number) => {
    if (!plan.brackets || plan.brackets.length === 0) {
      return plan.basePrice
    }
    const bracket = plan.brackets.find(
      (b) => lives >= b.min && (b.max === null || lives <= b.max)
    )
    return bracket ? bracket.price : plan.basePrice
  }

  // Função para obter o bracket atual baseado no número de vidas
  const getCurrentBracket = (plan: Plan, lives: number) => {
    if (!plan.brackets || plan.brackets.length === 0) {
      return null
    }
    return plan.brackets.find(
      (b) => lives >= b.min && (b.max === null || lives <= b.max)
    )
  }


  // Função para obter features incluídas como lista de strings
  const getIncludedFeatures = (plan: Plan) => {
    return plan.features
      .filter(f => f.included)
      .map(f => f.name)
  }

  // Obter faixas de vidas disponíveis (mínimo e máximo de todos os planos)
  // Considerar apenas brackets que NÃO exigem callCommercial
  const livesRange = useMemo(() => {
    if (!plans || plans.length === 0) return { min: 1, max: 30 }

    let minLives = Infinity
    let maxLives = 0

    plans.forEach((plan: Plan) => {
      // Filtrar apenas brackets que não exigem callCommercial
      const nonCommercialBrackets = plan.brackets?.filter((b: any) => !b.callCommercial) || []

      nonCommercialBrackets.forEach((bracket: any) => {
        if (bracket.min < minLives) minLives = bracket.min
        if (bracket.max !== null && bracket.max > maxLives) {
          maxLives = bracket.max
        } else if (bracket.max === null) {
          // Se algum bracket não tem max, precisamos encontrar o último bracket antes do primeiro callCommercial
          const firstCommercialBracket = plan.brackets?.find((b: any) => b.callCommercial)
          if (firstCommercialBracket) {
            // Usar o min - 1 do primeiro bracket comercial como limite
            maxLives = Math.max(maxLives, firstCommercialBracket.min - 1)
          } else {
            // Se não há bracket comercial, usar 100 como máximo para o seletor
            maxLives = Math.max(maxLives, 100)
          }
        }
      })
    })

    return { min: minLives === Infinity ? 1 : minLives, max: Math.max(maxLives || 30, 99999) }
  }, [plans])

  const handleSelectPlan = (plan: Plan) => {
    setSelectedPlan(plan.slug)

    if (!plan.slug) {
      console.error('❌ [AssinaturaV2Page] Plano sem slug:', plan);
      // Fallback para ID se não tiver slug
      if (plan.id) {
        console.warn('⚠️ [AssinaturaV2Page] Usando ID como fallback para navegação');
        router.push(`/assinatura/checkout?planId=${plan.id}&lives=${selectedLives}`)
        return
      }
      alert('Erro ao selecionar plano: Dados do plano incompletos');
      return;
    }

    // Passar ambos para garantir (slug é preferencial, id é fallback)
    router.push(`/assinatura/checkout/${plan.slug}/${selectedLives}`)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5FAFA] flex items-center justify-center">
        <p className="text-gray-500">Carregando planos...</p>
      </div>
    )
  }

  if (error) {
    console.error('❌ [AssinaturaV2Page] Erro ao carregar planos:', error);
    return (
      <div className="min-h-screen bg-[#F5FAFA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Erro ao carregar planos. Por favor, tente novamente.</p>
          <p className="text-sm text-gray-500">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
        </div>
      </div>
    )
  }

  if (!plans || plans.length === 0) {
    console.warn('⚠️ [AssinaturaV2Page] Nenhum plano retornado da API');
    return (
      <div className="min-h-screen bg-[#F5FAFA] flex items-center justify-center">
        <p className="text-gray-500">Nenhum plano disponível no momento.</p>
      </div>
    )
  }

  return (
    <div className="relative w-full min-h-screen bg-[#F5FAFA] overflow-x-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Ellipse 4 */}
        <div
          className="absolute w-[699px] h-[699px] -left-[323.32px] -top-[159.1px] bg-white rounded-full filter blur-[200px]"
        />

        {/* Ellipse 1 */}
        <div
          className="absolute w-[699px] h-[699px] left-[1107.29px] top-[1343.2px] bg-white rounded-full filter blur-[200px]"
        />

        {/* Ellipse 2 */}
        <div
          className="absolute w-[699px] h-[699px] -left-[329.27px] top-[2200.6px] bg-white rounded-full filter blur-[200px]"
        />

        {/* Ellipse 3 */}
        <div
          className="absolute w-[699px] h-[699px] left-[1024.58px] top-[4134.56px] bg-[#F0FAFA] rounded-full filter blur-[200px]"
        />
      </div>

      {/* Header Section */}
      <div className="relative z-10">

        {/* Hero Section */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
          <div className="relative overflow-hidden bg-[#5AE4A7]/5 backdrop-blur-md rounded-2xl border border-[#5AE4A7]/20 shadow-md px-4 py-6 md:px-8 md:py-8 mx-auto max-w-4xl">
            {/* Decoração interna de fundo (Blob suave) */}
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#5AE4A7] opacity-10 rounded-full blur-2xl pointer-events-none"></div>
            <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[#5AE4A7] opacity-10 rounded-full blur-2xl pointer-events-none"></div>
            
            <div className="relative z-10 flex flex-col items-center">
              {/* Main Title */}
              <div className="text-center mb-3">
                <div className="inline-block mb-3">
                  <span className="px-3 py-1 bg-[#143F42] text-[#5AE4A7] text-[10px] md:text-xs font-bold tracking-wide uppercase rounded-full shadow-sm">
                    Solução Completa em SST
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-[#0B2110] leading-tight max-w-3xl mx-auto mb-3">
                  Todos os exames exigidos pelo{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5AE4A7] to-[#143F42]">governo</span>
                </h1>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-2 w-full sm:w-auto">
                <button
                  onClick={() => {
                    const plansSection = document.getElementById('plans-section');
                    plansSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full sm:w-auto px-8 py-3.5 bg-[#5AE4A7] text-[#0B2110] rounded-xl text-base font-bold hover:bg-[#4ade80] transition-all shadow-md hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center group"
                >
                  Ver Planos
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
                <a
                  href={`https://wa.me/${(() => {
                    const raw = contactInfo?.fone || '+55-11-5178-5947';
                    let clean = raw.replace(/\D/g, '');
                    if (clean.length >= 10 && clean.length <= 11 && !clean.startsWith('55')) {
                      clean = '55' + clean;
                    }
                    return clean;
                  })()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 py-3.5 bg-white text-[#0B2110] border-2 border-[#143F42] rounded-xl text-base font-bold hover:bg-[#143F42] hover:text-white transition-all flex items-center justify-center"
                >
                  Falar com Especialista
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div id="plans-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 scroll-mt-20">
          <div className="text-center mb-16 md:mb-20">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#0B2110] leading-tight max-w-2xl mx-auto">
              Os melhores preços e o melhor serviço agora por assinatura
            </h2>
          </div>

          {/* Seletor de Quantidade de Vidas movido para os cards */}

          {/* Pricing Cards */}
          {/* Container externo para esconder a barra de rolagem no mobile e permitir o estilo bleed da tela */}
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 relative group slider-container">
            {/* Indicadores Mobile Clicáveis: Deslize ou clique para ver mais */}
            {canScrollLeft && (
              <button 
                onClick={() => scrollSlider('left')}
                className="absolute top-1/2 left-2 sm:hidden z-20 transform -translate-y-1/2 bg-white rounded-full shadow-[0_4px_15px_rgba(0,0,0,0.15)] p-3 border border-[#5AE4A7]/60 hover:bg-[#F5FAFA] transition-all flex items-center justify-center animate-fade-in"
                aria-label="Rolar para a esquerda"
              >
                <svg className="w-6 h-6 text-[#143F42]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {canScrollRight && (
              <button 
                onClick={() => scrollSlider('right')}
                className="absolute top-1/2 right-2 sm:hidden z-20 transform -translate-y-1/2 bg-white rounded-full shadow-[0_4px_15px_rgba(0,0,0,0.15)] p-3 border border-[#5AE4A7]/60 hover:bg-[#F5FAFA] transition-all flex items-center justify-center animate-fade-in"
                aria-label="Rolar para a direita"
              >
                <svg className="w-6 h-6 text-[#143F42]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Sombras de borda para dar ideia de continuidade no scroll (Mobile) */}
            <div className="absolute top-0 bottom-8 left-0 w-8 bg-gradient-to-r from-[#F5FAFA] to-transparent z-10 pointer-events-none sm:hidden"></div>
            <div className="absolute top-0 bottom-8 right-0 w-8 bg-gradient-to-l from-[#F5FAFA] to-transparent z-10 pointer-events-none sm:hidden"></div>

            <div 
              className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 overflow-x-auto sm:overflow-x-visible pt-6 pb-8 snap-x snap-mandatory hide-scrollbar relative z-0"
              onScroll={handleScroll}
              ref={(scrollContainer) => {
                scrollContainerRef.current = scrollContainer;
                // Auto scroll para o card recomendado no mobile após montagem
                if (scrollContainer && window.innerWidth < 640 && !scrollContainer.dataset.scrolled) {
                  setTimeout(() => {
                    const recommendedCard = scrollContainer.querySelector('[data-recommended="true"]');
                    if (recommendedCard) {
                      const containerCenter = scrollContainer.offsetWidth / 2;
                      const cardCenter = (recommendedCard as HTMLElement).offsetLeft + ((recommendedCard as HTMLElement).offsetWidth / 2);
                      scrollContainer.scrollTo({
                        left: cardCenter - containerCenter,
                        behavior: 'smooth'
                      });
                      setTimeout(handleScroll, 300); // Atualiza os botões após o scroll suave
                    }
                  }, 100);
                  scrollContainer.dataset.scrolled = 'true';
                }
              }}
            >
              {plans.map((plan: Plan, index: number) => {
                const currentPrice = getPriceForLives(plan, selectedLives)
                const currentBracket = getCurrentBracket(plan, selectedLives)
                const isRecommended = index === 1 // Convencional é recomendado
                const hasCallCommercial = currentBracket?.callCommercial || false

                return (
                  <div
                    key={plan.id}
                    data-recommended={isRecommended}
                    className={`relative p-5 sm:p-6 bg-white shadow-xl rounded-2xl transition-all sm:hover:shadow-2xl flex-none w-[85vw] sm:w-auto snap-center ${isRecommended ? 'ring-2 ring-[#5AE4A7] ring-offset-2 sm:scale-105' : 'border border-[#5AE4A7]/10'
                      }`}
                  >
                  {isRecommended && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-1.5 bg-gradient-to-r from-[#0B2110] to-[#143F42] text-[#5AE4A7] text-[11px] font-black uppercase tracking-widest rounded-full shadow-[0_4px_15px_rgba(20,63,66,0.3)] border border-[#5AE4A7]/30 flex items-center justify-center whitespace-nowrap z-10">
                      Mais Escolhido
                    </div>
                  )}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-[#0B2110] mb-1 text-center">{plan.name}</h3>
                    <p className="text-sm text-[#627065] text-center leading-relaxed">{plan.description}</p>
                  </div>
                  <div className="mb-4 pb-4 border-b border-[#5AE4A7]/10">
                    <div className="text-center">
                      {hasCallCommercial ? (
                        <>
                          <span className="text-3xl font-bold text-[#0B2110] block mb-2">
                            Sob consulta
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-[#0B2110]">
                            R$ {currentPrice.toFixed(2).replace('.', ',')}
                          </span>
                          <span className="text-base font-medium text-[#627065]"> / mensais</span>
                        </>
                      )}
                      
                      {/* Seletor de Vidas dentro do card */}
                      <div className="mt-4 bg-[#F5FAFA] rounded-xl p-3 border border-[#5AE4A7]/10">
                        <label className="block text-xs font-semibold text-[#627065] mb-2">Quantidade de vidas</label>
                        <div className="flex items-center justify-between gap-2 max-w-[200px] mx-auto">
                          <button
                            onClick={() => {
                              trackClarityEvent(`planos_vidas_menos: ${plan.name}`)
                              setSelectedLives(Math.max(livesRange.min, selectedLives - 1))
                            }}
                            className="w-8 h-8 flex items-center justify-center bg-white border border-[#5AE4A7]/30 text-[#0B2110] rounded-lg hover:bg-[#5AE4A7]/10 transition-colors shadow-sm"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min={livesRange.min}
                            max={livesRange.max}
                            value={selectedLives}
                            onChange={(e) => {
                              trackClarityEvent(`planos_vidas_manual: ${plan.name}`)
                              const value = parseInt(e.target.value) || livesRange.min
                              setSelectedLives(Math.max(livesRange.min, Math.min(livesRange.max, value)))
                            }}
                            className="w-16 px-2 py-1.5 border border-[#5AE4A7]/30 rounded-lg text-center font-bold text-[#0B2110] focus:outline-none focus:ring-1 focus:ring-[#5AE4A7]"
                          />
                          <button
                            onClick={() => {
                              trackClarityEvent(`planos_vidas_mais: ${plan.name}`)
                              setSelectedLives(Math.min(livesRange.max, selectedLives + 1))
                            }}
                            className="w-8 h-8 flex items-center justify-center bg-[#5AE4A7] text-white rounded-lg hover:bg-[#4ade80] transition-colors shadow-sm"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6 sm:max-h-80 sm:overflow-y-auto pr-1">
                    {plan.features.map((feature, idx) => {
                      // Verificar se é exame clínico
                      const isClinicalExam = feature.key === 'clinical_exams' || feature.name.toLowerCase().includes('exame clínico');
                      const hasExtraCost = feature.extraCost;
                      const isNotIncluded = !feature.included;

                      // Determinar se mostra X vermelho (cobrado à parte ou não incluso)
                      let showRedX = false;
                      if (isClinicalExam && (hasExtraCost || isNotIncluded)) {
                        // Exame clínico com custo extra OU não incluso: X vermelho
                        showRedX = true;
                      } else if (isNotIncluded && !isClinicalExam) {
                        // Feature não inclusa (não é exame clínico): X vermelho
                        showRedX = true;
                      }

                      return (
                        <li key={idx} className="flex items-start">
                          {showRedX ? (
                            // X vermelho para exames clínicos que serão cobrados ou features não inclusas
                            <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            // Check verde para recursos inclusos
                            <svg className="w-5 h-5 text-[#5AE4A7] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          <div className="flex-1">
                            <span className={`text-sm leading-relaxed ${feature.included ? 'text-[#627065]' : 'text-gray-400'}`}>
                              {feature.name}
                            </span>
                            {isClinicalExam && (
                              <span className={`block text-xs mt-1 font-medium ${showRedX ? 'text-red-500' : 'text-[#5AE4A7]'
                                }`}>
                                {showRedX ? 'Pagos à parte' : 'Inclusos no plano'}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {hasCallCommercial || selectedLives > 30 ? (
                    <a
                      href={`https://wa.me/${(() => {
                        const raw = contactInfo?.fone || '+55-11-5178-5947';
                        let clean = raw.replace(/\D/g, '');
                        if (clean.length >= 10 && clean.length <= 11 && !clean.startsWith('55')) {
                          clean = '55' + clean;
                        }
                        return clean;
                      })()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3 px-6 rounded-xl text-base font-semibold transition-all shadow-md bg-[#0B2110] text-white hover:bg-gray-800 hover:shadow-lg flex items-center justify-center"
                    >
                      Fale com um Consultor
                      <svg className="w-5 h-5 inline ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </a>
                  ) : (
                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className={`w-full py-3 px-6 rounded-xl text-base font-semibold transition-all shadow-md ${selectedPlan === plan.slug
                        ? 'bg-[#0B2110] text-white'
                        : isRecommended
                          ? 'bg-[#5AE4A7] text-white hover:bg-[#4ade80] hover:shadow-lg'
                          : 'bg-[#5AE4A7] text-white hover:bg-[#4ade80] hover:shadow-lg'
                        }`}
                    >
                      Assinar
                      <svg className="w-5 h-5 inline ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              )
            })}
            </div>
          </div>
        </div>

        {/* What is required section */}
        <div className="relative bg-[#143F42] py-16">
          <div className="text-center px-8 mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F5FAFA] leading-tight max-w-3xl mx-auto">
              O que é necessário para atender a legislação
            </h2>
          </div>

          {/* Features Section */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Visitas técnicas */}
              <div className="text-center">
                <div className="w-20 h-20 bg-[#5AE4A7] bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-[#5AE4A7] rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#F5FAFA] mb-3">Visitas técnicas</h3>
                <p className="text-sm text-[#F5FAFA]/90 leading-relaxed">
                  Realização de visitas técnicas periódicas para avaliação dos ambientes de trabalho e identificação de riscos ocupacionais.
                </p>
              </div>

              {/* Eventos do E-social */}
              <div className="text-center">
                <div className="w-20 h-20 bg-[#5AE4A7] bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-[#5AE4A7] rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#F5FAFA] mb-3">Eventos do E-social</h3>
                <p className="text-sm text-[#F5FAFA]/90 leading-relaxed">
                  Envio automático de eventos para o e-social, garantindo conformidade com a legislação trabalhista e previdenciária.
                </p>
              </div>

              {/* Todos os Exames */}
              <div className="text-center">
                <div className="w-20 h-20 bg-[#5AE4A7] bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-[#5AE4A7] rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#F5FAFA] mb-3">Todos os Exames</h3>
                <p className="text-sm text-[#F5FAFA]/90 leading-relaxed">
                  Exames admissionais, demissionais, periódicos, de mudança de função e complementares. Tudo que sua empresa precisa para estar em conformidade.
                </p>
              </div>

              {/* Documentação Completa */}
              <div className="text-center">
                <div className="w-20 h-20 bg-[#5AE4A7] bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-[#5AE4A7] rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#F5FAFA] mb-3">Documentação Completa</h3>
                <p className="text-sm text-[#F5FAFA]/90 leading-relaxed">
                  PCMSO, LTCAT, ASO digital e toda documentação necessária para atender a legislação de saúde ocupacional.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tudo em um só lugar */}
        <div className="bg-gradient-to-b from-[#F5FAFA] to-white py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B2110] leading-tight mb-4">
                Tudo em um só lugar
              </h2>
              <p className="text-lg text-[#627065] max-w-2xl mx-auto">
                Uma plataforma completa para gerenciar toda a saúde ocupacional da sua empresa
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#5AE4A7]/10 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#5AE4A7]/10 rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <svg className="w-8 h-8 text-[#5AE4A7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#0B2110] mb-4 text-center">Documentação</h3>
                <p className="text-sm text-[#627065] leading-relaxed text-center">
                  PCMSO, LTCAT, ASO digital e toda documentação necessária para atender a legislação trabalhista de forma completa e organizada.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#5AE4A7]/10 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#5AE4A7]/10 rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <svg className="w-8 h-8 text-[#5AE4A7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#0B2110] mb-4 text-center">Exames</h3>
                <p className="text-sm text-[#627065] leading-relaxed text-center">
                  Admissionais, demissionais, periódicos e complementares. Todos os exames exigidos pelo governo em uma única plataforma.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#5AE4A7]/10 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#5AE4A7]/10 rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <svg className="w-8 h-8 text-[#5AE4A7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#0B2110] mb-4 text-center">Relação com e-social</h3>
                <p className="text-sm text-[#627065] leading-relaxed text-center">
                  Integração completa com o e-social. Eventos e informações sincronizados automaticamente para garantir conformidade.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq-section">
          <FaqSection />
        </div>

        {/* Main Description */}

      </div>


    </div>
  )
}
